// @flow
import fp from "lodash/fp"
import * as R from "ramda"
import { createSelector } from "reselect"
import { getValues } from "redux-form"

import Price from "common/records/Price"
import { formatToSaveBooking } from "./services/formatter"
import { splicePassengersForFlights } from "./services/splicePassengers"
import { allPassengersForFlight } from "./services/orderedCheckers"
import {
  getOriginalPassengersCount,
  getHandBagsWithPrioBoardingAllowed,
} from "../../../states/reducer"
import { getJourney } from "../../../journey/reducer"
import { passengersSelector } from "../../../reservation/selectors/passengersSelectors"
import type { Action } from "./actions"
import { cabinBagAllowedForAdultsSelector } from "../../../baggage/selectors/baggageSelectors"
import type {
  PriorityBoarding,
  PriorityBoardingData,
  PriorityBoardingOrdered,
} from "booking/services/additionalServices/services/priorityBoarding/services/types"

const DEFAULT_STATE: PriorityBoarding = {
  type: 0, // Will be used for general additional services
  enabled: false,
  data: {},
  ordered: {},
}

const priorityBoardingReducer = (state: PriorityBoarding = DEFAULT_STATE, action: Action) => {
  switch (action.type) {
    case "SET_PRIORITY_BOARDING_DATA":
      return action.payload
    case "SET_PRIORITY_BOARDING_ORDERED":
      return { ...state, ordered: action.payload }
    case "SET_PRIORITY_BOARDING_FOR_PASSENGER_ONE_FLIGHT":
      return fp.setWith(
        Object, // Ensure to be object with number keys
        ["ordered", action.payload.flightId, action.payload.passenger],
        action.payload.ordered,
        state,
      )
    case "SET_PRIORITY_BOARDING_FOR_PASSENGERS_ONE_FLIGHT":
      return fp.set(
        "ordered",
        {
          ...state.ordered,
          [action.payload.flightId]: fp.reduce(
            (acc, passengerId) => fp.set(passengerId, fp.get(["payload", "ordered"], action), acc),
            Object,
          )(action.payload.passengers),
        },
        state,
      )
    case "SET_PRIORITY_BOARDING_FOR_PASSENGER_ALL_FLIGHTS":
      return fp.set(
        "ordered",
        fp.reduce(
          (acc, flightId) =>
            // $FlowFixMe
            fp.setWith(Object, [flightId, action.payload.passenger], action.payload.ordered, acc),
          state.ordered,
          action.payload.flightsIds,
        ),
        state,
      )

    case "REMOVE_PRIORITY_BOARDING_FOR_PASSENGERS": {
      const flightIds = Object.keys(state.ordered)
      const newOrdered = fp.reduce(
        (accPap, passengerIndex) =>
          fp.reduce(
            (accFlight, flightId) =>
              fp.setWith(Object, [flightId, passengerIndex], false, accFlight),
            accPap,
            flightIds,
          ),
        state.ordered,
        action.payload.passengerIndexes,
      )
      return fp.set("ordered", newOrdered, state)
    }
    case "SPLICE_PASSENGERS":
      return fp.set(
        "ordered",
        splicePassengersForFlights(
          action.payload.index,
          action.payload.newNumOfPassengers,
          state.ordered,
        ),
        state,
      )
    default:
      return state
  }
}

export default priorityBoardingReducer

// can't import State type because of circular import
export const getPriorityBoarding = fp.getOr(DEFAULT_STATE, [
  "reservation",
  "additionalServices",
  "priorityBoarding",
])

export const getPriorityBoardingData: $FlowFixMe = createSelector(
  getPriorityBoarding,
  priorityBoarding => priorityBoarding.data,
)

export const getPriorityBoardingOrdered: $FlowFixMe = createSelector(
  getPriorityBoarding,
  priorityBoarding => priorityBoarding.ordered,
)

export const anyPriorityBoardingOrderedSelector: $FlowFixMe = createSelector(
  getPriorityBoarding,
  priorityBoarding => {
    const passengerOrders = fp.values(priorityBoarding.ordered)
    const anyPriorityBoardingOrdered = passengerOrders
      .map(x => fp.values(x).some(Boolean))
      .some(Boolean)

    return anyPriorityBoardingOrdered
  },
)

export const getPriorityBoardingEnabled: $FlowFixMe = createSelector(
  getPriorityBoarding,
  priorityBoarding => priorityBoarding.enabled,
)

export const getPriorityBoardingPricePassengerFlight: $FlowFixMe = createSelector(
  getPriorityBoarding,
  priorityBoarding => flightId =>
    priorityBoarding.data[flightId] && priorityBoarding.data[flightId].price,
)

export const hasOrderedPriorityBoardingPassengerFlight = (
  priorityBoarding: PriorityBoardingOrdered,
) => (flightId: string) => (passengerIndex: number) =>
  priorityBoarding[flightId] && priorityBoarding[flightId][passengerIndex]

export const hasOrderedPriorityBoardingPassengerFlightSelector: $FlowFixMe = createSelector(
  getPriorityBoardingOrdered,
  hasOrderedPriorityBoardingPassengerFlight,
)

export const getReservationPriorityBoardingPassengersSelector: $FlowFixMe = createSelector(
  getPriorityBoarding,
  getJourney,
  passengersSelector,
  (priorityBoarding, journey, passengers) => {
    const flights = journey.getFlights()
    const passengerValues = passengers.map(passenger => getValues(passenger))

    return flights.reduce((prev, flight) => {
      const flightId = flight.get("id")
      const passengersWithPBOrderedInfo = passengerValues.map((passenger, index) => ({
        ...passenger,
        hasOrderedPB: Boolean(
          priorityBoarding.ordered[flightId] && priorityBoarding.ordered[flightId][String(index)],
        ),
      }))

      return { ...prev, [flightId]: passengersWithPBOrderedInfo }
    }, {})
  },
)

export const hasOrderedPriorityBoardingAllPassengersFlight = (
  priorityBoardingOrdered: PriorityBoardingOrdered,
) => (flightId: string) => (passengersLength: number) => {
  const ordered = priorityBoardingOrdered[flightId]
  const orderedAllPassengers = ordered && Object.keys(ordered).length === passengersLength

  return (
    orderedAllPassengers && !Object.keys(ordered).some(passenger => !ordered[Number(passenger)])
  )
}

export const hasOrderedPriorityBoardingAllPassengersFlightSelector: $FlowFixMe = createSelector(
  getPriorityBoardingOrdered,
  hasOrderedPriorityBoardingAllPassengersFlight,
)

export const cheapestPriorityBoardingPriceSelector: $FlowFixMe = createSelector(
  getPriorityBoarding,
  priorityBoarding => {
    const priorityBoardingData = Object.values(priorityBoarding.data)
    // Return 0 if no pb data
    return priorityBoardingData.length > 0
      ? // $FlowFixMe: error revealed by updating Flow and lodash libdef, please fix
        fp.minBy(x => x.price.amount, priorityBoardingData).price.amount
      : 0
  },
)

// Needs to be here, because of circular imports
const passengersFormCountSelector = state =>
  fp.getOr(undefined, ["form", "reservation", "passengers", "length"], state)

const passengersCountSelector: $FlowFixMe = createSelector(
  passengersFormCountSelector,
  getOriginalPassengersCount,
  (passengersFormCount, originalPassengersCount) => passengersFormCount || originalPassengersCount,
)

// Check, if all passengers has ordered Priority Boarding for one flight
export const hasAllPassengersPriorityBoardingOrderedForFlight: $FlowFixMe = createSelector(
  getPriorityBoardingOrdered,
  passengersCountSelector,
  allPassengersForFlight,
)

export const hasAnyPassengersPriorityBoardingOrderedForFlight: $FlowFixMe = createSelector(
  getPriorityBoardingOrdered,
  passengersCountSelector,
  (priorityBoardingOrdered: PriorityBoardingOrdered, numOfPassengers: number) => (
    flightId: string,
  ): boolean =>
    fp.some(
      passengerIndex =>
        fp.compose(
          Boolean,
          fp.getOr(false, [flightId, passengerIndex]),
        )(priorityBoardingOrdered),
      fp.range(0, numOfPassengers),
    ),
)

// Formatted for Save booking
export const priorityBoardingFormattedSelector: $FlowFixMe = createSelector(
  getPriorityBoarding,
  passengersCountSelector,
  (priorityBoarding, numOfPassengers = 1) =>
    formatToSaveBooking(numOfPassengers)(fp.get("ordered", priorityBoarding)),
)
// Formatted for Log
export const priorityBoardingFormattedToLogSelector: $FlowFixMe = createSelector(
  getPriorityBoarding,
  passengersCountSelector,
  priorityBoarding =>
    fp.toPairs(priorityBoarding.ordered).map(value => ({
      flight_id: value[0],
      passengers: fp.values(value[1]),
    })),
)

export const isEligibleForPriorityBoardingAncillarySelector: $FlowFixMe = createSelector(
  getJourney,
  getPriorityBoarding,
  getHandBagsWithPrioBoardingAllowed,
  cabinBagAllowedForAdultsSelector,
  (journey, priorityBoarding, handBagsWithPrioBoardingAllowed, cabinBagAllowedForAdults) => {
    const handBagWithPrioBoardingFlightIds = journey.getPriorityBoardingFlights().map(R.prop("id"))

    const handBagWithPrioOnly = R.compose(
      R.isEmpty,
      R.omit(handBagWithPrioBoardingFlightIds),
    )(priorityBoarding.data)

    if (
      // Priority Boarding Ancillary not prepared for multicity layout
      journey.isMulticity() ||
      // If combined itineraries are on, and only prio is through cabin bag, need to have allowed hand bag
      (handBagWithPrioOnly && (handBagsWithPrioBoardingAllowed && !cabinBagAllowedForAdults))
    )
      return false

    const flights = journey.getFlights()

    // allowed only if at least one flight has pb
    return flights
      .map(flight => flight.get("id"))
      .map(flightId => priorityBoarding.data[flightId])
      .some(flightPBInfo => flightPBInfo && flightPBInfo.isPossible)
  },
)

export const calculatePriorityBoardingPrice = (
  ordered: PriorityBoardingOrdered,
  data: PriorityBoardingData,
  isEligibleForPriorityBoardingAncillary: boolean,
  prioBoardingFlightsIds: string[],
) =>
  isEligibleForPriorityBoardingAncillary
    ? R.compose(
        Price.sum,
        R.map(([flightId, flightPassengerOrdered]) =>
          R.compose(
            Price.sum,
            R.map(R.always(data[flightId].price)),
            R.filter(Boolean),
            Object.values,
          )(flightPassengerOrdered),
        ),
        R.toPairs, // [ [OX0, { 0: true, 1: false }], [OX1, { 0: true, 1: true }] ]
        // Don't count with priority boarding for flights, which are ordered as cabin bag
        R.omit(prioBoardingFlightsIds),
      )(ordered)
    : new Price({ amount: 0 })

// FIXME: Tests
// FIXME: This should be the only Price related exposed selector from this file
export const priorityBoardingOrderedPriceSelector: $FlowFixMe = createSelector(
  getPriorityBoarding,
  isEligibleForPriorityBoardingAncillarySelector,
  getJourney,
  (priorityBoarding, isEligibleForPriorityBoardingAncillary, journey) =>
    calculatePriorityBoardingPrice(
      priorityBoarding.ordered,
      priorityBoarding.data,
      isEligibleForPriorityBoardingAncillary,
      journey.getPriorityBoardingFlights().map(R.prop("id")),
    ),
)
